import { StateCreator } from "zustand";

/**
 * Chat slice state.
 *
 * NOTE: This slice only holds client-only UI state. TalkJS data comes
 * from the server via React Query.
 */
export type ChatSliceState = {
  /** Whether the chat sheet is open */
  isChatOpen: boolean;
  /**
   * Optional programId override. When set, `useTalkJSData` uses this instead
   * of the user's first-enrolled program to resolve `chat_status` /
   * `chat_circle_ids`. Video pages set it to the meeting's program so chat
   * is scoped to the program the meeting actually belongs to; null means
   * fall back to first-enrolled.
   */
  programIdOverride: number | null;
  /**
   * Per-program circle selection for the dashboard chat surface.
   *
   * Keyed by `programId`, the value is the `circleId` whose conversation
   * the dashboard should render for that program. Persisted via the
   * platform store's localStorage layer so a user's choice survives
   * reloads, but kept per-program so a multi-program user's selection
   * in program A doesn't leak into program B.
   *
   * Single-circle users skip the picker entirely (auto-resolve in
   * `useResolvedChatCircle`), so this map only matters for users who
   * actually have something to choose between. Stale entries (the user
   * was removed from the selected circle) are cleared by the resolver
   * when it detects the mismatch.
   */
  selectedCircleByProgram: Record<number, number>;
  /**
   * Counter that, when bumped, forces `<ChatSession>`'s memoized
   * `syncUser` factory to produce a new function reference, which in
   * turn triggers `<Session>` (from `@talkjs/react`) to destroy and
   * recreate its underlying `Talk.Session` in place.
   *
   * `<Session>` itself does NOT remount; its children stay mounted.
   * This is the recovery path used by `<ChatboxStuckDetector>` when the
   * Chatbox stays on its `loadingComponent` past a wall-clock timeout
   * (silent WS handshake failure). See
   * docs/chat-stuck-spinner-fix-plan.md §5.4 / §10.1 for the mechanism
   * and why a React `key` re-mount would not work here (it would
   * unmount the entire AppLayout/VideoLayout subtree, including the
   * meeting page and Chime providers).
   *
   * Not persisted across reloads — fresh page, fresh attempt.
   */
  sessionRetryKey: number;
};

/**
 * Chat slice actions.
 */
export type ChatSliceActions = {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  setProgramIdOverride: (programId: number | null) => void;
  setSelectedCircle: (programId: number, circleId: number) => void;
  clearSelectedCircle: (programId: number) => void;
  /**
   * Bump `sessionRetryKey` to trigger an in-place TalkJS Session
   * recycle. Wired to the "Try again" button on `<ChatErrorState>` via
   * `<ChatboxStuckDetector>`. See chatSlice.ts state docstring.
   */
  bumpSessionRetryKey: () => void;
};

/**
 * Full chat slice type.
 */
export type ChatSlice = ChatSliceState & ChatSliceActions;

/**
 * Initial state for chat slice.
 */
export const initialChatState: ChatSliceState = {
  isChatOpen: false,
  programIdOverride: null,
  selectedCircleByProgram: {},
  sessionRetryKey: 0,
};

/**
 * Creates the chat slice for Zustand store.
 *
 * @example
 * ```typescript
 * const useStore = create<ChatSlice>()((...a) => ({
 *   ...createChatSlice(...a),
 * }));
 *
 * // In a component:
 * const isChatOpen = useStore((s) => s.isChatOpen);
 * const openChat = useStore((s) => s.openChat);
 * ```
 */
export const createChatSlice: StateCreator<ChatSlice, [], [], ChatSlice> = (
  set
) => ({
  ...initialChatState,

  openChat: () => set({ isChatOpen: true }),

  closeChat: () => set({ isChatOpen: false }),

  toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),

  setProgramIdOverride: (programId) => set({ programIdOverride: programId }),

  setSelectedCircle: (programId, circleId) =>
    set((state) => ({
      selectedCircleByProgram: {
        ...state.selectedCircleByProgram,
        [programId]: circleId,
      },
    })),

  clearSelectedCircle: (programId) =>
    set((state) => {
      const next = { ...state.selectedCircleByProgram };
      delete next[programId];
      return { selectedCircleByProgram: next };
    }),

  bumpSessionRetryKey: () =>
    set((state) => ({ sessionRetryKey: state.sessionRetryKey + 1 })),
});

// --- Selectors ---
export const chat = {
  sheet: (s: ChatSlice) => ({
    isOpen: s.isChatOpen,
    open: s.openChat,
    close: s.closeChat,
    toggle: s.toggleChat,
  }),
  selection: (s: ChatSlice) => ({
    selectedCircleByProgram: s.selectedCircleByProgram,
    setSelectedCircle: s.setSelectedCircle,
    clearSelectedCircle: s.clearSelectedCircle,
  }),
  sessionRetry: (s: ChatSlice) => ({
    key: s.sessionRetryKey,
    bump: s.bumpSessionRetryKey,
  }),
};
